import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka } from '@/utils/number';
import { pcmpMisBatchesQueryOptions } from '../../api/pcmpMis.api';
import { PCMP_MIS_EMPTY_MESSAGE } from '../../constants/pcmpMis.constants';
import { PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import type { PcmpMisFilterController } from '../../hooks/usePcmpMisFilters';
import { toPcmpQueueSearch } from '../../hooks/usePcmpQueueFilters';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import type { PcmpMisBatch, PcmpMisBatches } from '../../types/pcmpMis.types';
import { PcmpEmptyCell } from '../PcmpQueue/PcmpQueueCells';
import { PcmpBatchItemsCell, PcmpBatchStepCell } from '../PcmpBatch/PcmpBatchCells';
import { PcmpBatchStatusPill } from './PcmpMisBatchCells';

const columns: ColumnDef<PcmpMisBatch>[] = [
  {
    id: 'batchNo',
    header: 'Batch',
    cell: ({ row }) => (
      <span className="font-mono font-medium whitespace-nowrap">#{row.original.batchNo}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <PcmpBatchStatusPill status={row.original.status} />,
  },
  {
    id: 'channel',
    header: 'Payer',
    cell: ({ row }) =>
      row.original.channel ? PCMP_CHANNEL_LABELS[row.original.channel] : <PcmpEmptyCell />,
  },
  {
    id: 'items',
    header: () => <div className="text-right">Proposals</div>,
    cell: ({ row }) => (
      <div className="text-right">
        <PcmpBatchItemsCell batch={row.original} />
      </div>
    ),
  },
  {
    id: 'amount',
    header: () => <div className="text-right">Amount</div>,
    cell: ({ row }) => (
      <div className="text-right whitespace-nowrap tabular-nums">
        {formatTaka(row.original.totalAmount)}
      </div>
    ),
  },
  {
    id: 'staged',
    header: 'Staged',
    cell: ({ row }) => <PcmpBatchStepCell by={row.original.stagedBy} at={row.original.stagedAt} />,
  },
  {
    id: 'published',
    header: 'Published',
    cell: ({ row }) => (
      <PcmpBatchStepCell by={row.original.publishedBy} at={row.original.publishedAt} />
    ),
  },
  {
    // Legacy LDS-803: the remark MIS gave when publishing the batch.
    id: 'publishComment',
    header: 'Publish Comment',
    cell: ({ row }) =>
      row.original.publishComment ? (
        <span
          className="line-clamp-2 block max-w-64 whitespace-pre-line"
          title={row.original.publishComment}
        >
          {row.original.publishComment}
        </span>
      ) : (
        <PcmpEmptyCell />
      ),
  },
];

function TotalsStrip({ totals }: { totals: PcmpMisBatches['totals'] }) {
  const items = [
    { label: 'All batches', total: totals.all },
    { label: 'Accounts', total: totals.accounts },
    { label: 'Marketing', total: totals.marketing },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {items.map(({ label, total }) => (
        <div key={label} className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-lg font-semibold tabular-nums">{formatTaka(total.amount)}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatNumber(total.batches)} batches · {formatNumber(total.count)} proposals
          </span>
        </div>
      ))}
    </div>
  );
}

/** The routing batches, newest first; a row opens the batch. */
export function PcmpMisBatchesView({ controller }: { controller: PcmpMisFilterController }) {
  const router = useRouter();
  const { batchesParams, appliedFilters, limit } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpMisBatchesQueryOptions(batchesParams),
  );

  useKeepPageInRange(batchesParams.page, data?.batches.meta.totalPages, controller.setPage);

  const openBatch = useCallback(
    (batch: PcmpMisBatch) => {
      void router.navigate({
        to: '/pcmp/mis/batches/$batchNo',
        params: { batchNo: batch.batchNo },
        // The batch opens on the members matching the same filters.
        search: toPcmpQueueSearch(appliedFilters),
      });
    },
    [router, appliedFilters],
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load the routing batches"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  return (
    <>
      {data ? <TotalsStrip totals={data.totals} /> : null}
      <DataTable
        data={data?.batches.items ?? []}
        columns={columns}
        isLoading={isLoading}
        fillHeight
        className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
        emptyIcon={Layers}
        emptyMessage={PCMP_MIS_EMPTY_MESSAGE.BATCHES}
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(batch) => (batch.status === 'STAGED' ? 'bg-warning/30' : undefined)}
        onRowClick={openBatch}
      />
      {data ? (
        <DataTablePagination
          page={data.batches.meta.page}
          totalPages={data.batches.meta.totalPages}
          total={data.batches.meta.total}
          limit={data.batches.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </>
  );
}
